import { useEffect, useState } from "react";

interface UrgencyBarProps {
  variant?: "bar" | "card";
}

export default function UrgencyBar({ variant = "bar" }: UrgencyBarProps) {
  const isCard = variant === "card";
  const STORAGE_KEY = "mujer-abundante-timer-expires";
  const TOTAL_SECONDS = 4 * 60;
  const [timeLeft, setTimeLeft] = useState(TOTAL_SECONDS);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const now = Date.now();

    let targetTime: number;
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > now) {
        targetTime = parsed;
      } else {
        setExpired(true);
        setTimeLeft(0);
        return;
      }
    } else {
      targetTime = now + TOTAL_SECONDS * 1000;
      sessionStorage.setItem(STORAGE_KEY, String(targetTime));
    }

    const update = () => {
      const diff = Math.max(0, Math.ceil((targetTime - Date.now()) / 1000));
      setTimeLeft(diff);
      if (diff <= 0) {
        setExpired(true);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const m = Math.floor(timeLeft / 60);
  const s = timeLeft % 60;

  // Card variant — big, dramatic, inside pricing card
  if (isCard) {
    return (
      <div
        className={`w-full rounded-lg border-2 px-4 py-5 md:px-6 md:py-6 text-center ${expired ? "border-red-500/50 bg-red-950/10" : "border-gold/50 bg-[#0D0D0D] animate-pulse-glow"}`}
      >
        {/* Label */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {!expired && (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
          )}
          <span
            className={`font-sans text-sm md:text-base font-bold tracking-wide uppercase ${expired ? "text-red-400" : "text-gold"}`}
          >
            {expired
              ? "Tiempo agotado — precio ahora $47"
              : "Oferta especial por tiempo limitado"}
          </span>
          {!expired && (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
          )}
        </div>

        {/* Big Timer */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mb-3">
          <div className="flex flex-col items-center">
            <span
              className={`font-sans text-4xl sm:text-5xl md:text-6xl font-bold tabular-nums leading-none ${expired ? "text-cream/20" : "text-gold"}`}
            >
              {String(m).padStart(2, "0")}
            </span>
            <span className="font-sans text-[10px] md:text-xs text-cream/40 mt-1 uppercase tracking-[0.15em]">
              min
            </span>
          </div>

          <span
            className={`font-sans text-3xl sm:text-4xl md:text-5xl font-light -mt-2 ${expired ? "text-cream/10" : "text-gold/60"}`}
          >
            :
          </span>

          <div className="flex flex-col items-center">
            <span
              className={`font-sans text-4xl sm:text-5xl md:text-6xl font-bold tabular-nums leading-none ${expired ? "text-cream/20" : "text-gold"}`}
            >
              {String(s).padStart(2, "0")}
            </span>
            <span className="font-sans text-[10px] md:text-xs text-cream/40 mt-1 uppercase tracking-[0.15em]">
              seg
            </span>
          </div>
        </div>

        {/* Sub-label */}
        {!expired && (
          <p className="font-sans text-xs md:text-sm text-cream/50 tracking-wide">
            El precio subirá al finalizar el contador
          </p>
        )}
      </div>
    );
  }

  // Bar variant — compact, full-width, at top of page
  return (
    <div
      className={`w-full border-b ${expired ? "border-red-900/40" : "border-gold/20"}`}
      style={{ backgroundColor: "#0A0A0A" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 md:py-3 flex items-center justify-center gap-3 md:gap-6 flex-wrap">
        {/* Left: label + dot */}
        <div className="flex items-center gap-2 shrink-0">
          {!expired && (
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
          )}
          <span
            className={`font-sans text-[11px] md:text-xs font-semibold tracking-wide uppercase ${expired ? "text-red-400" : "text-gold"}`}
          >
            {expired
              ? "Tiempo agotado — precio ahora $47"
              : "Oferta especial por tiempo limitado"}
          </span>
        </div>

        {/* Center: timer digits */}
        <div className="flex items-center gap-1.5 md:gap-2">
          <div className="flex items-baseline gap-0.5">
            <span className="font-sans text-lg md:text-xl font-bold tabular-nums text-gold">
              {String(m).padStart(2, "0")}
            </span>
            <span className="font-sans text-[9px] md:text-[10px] text-cream/40 uppercase tracking-wider">
              min
            </span>
          </div>
          <span className="font-sans text-lg md:text-xl font-light text-gold/70 -mt-0.5">
            :
          </span>
          <div className="flex items-baseline gap-0.5">
            <span className="font-sans text-lg md:text-xl font-bold tabular-nums text-gold">
              {String(s).padStart(2, "0")}
            </span>
            <span className="font-sans text-[9px] md:text-[10px] text-cream/40 uppercase tracking-wider">
              seg
            </span>
          </div>
        </div>

        {/* Right: sub-label or CTA hint */}
        {!expired && (
          <span className="hidden md:inline font-sans text-[10px] md:text-[11px] text-cream/40 tracking-wide">
            El precio subirá al finalizar el contador
          </span>
        )}
      </div>
    </div>
  );
}